import React from 'react';

interface User {
  username: string;
  email: string;
  fullname: string;
}

interface UserTableForRecipientProps {
  users: User[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  currentPage: number;
  pageSize: number;
  selectedUsernames: string[];
  onToggleRecipient: (username: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  isSelectingAll?: boolean;
}

const UserTableForRecipient: React.FC<UserTableForRecipientProps> = ({
  users,
  isLoading,
  isError,
  error,
  currentPage,
  pageSize,
  selectedUsernames,
  onToggleRecipient,
  onToggleSelectAll,
  isSelectingAll = false,
}) => {
  const isRecipientSelected = (username: string) =>
    selectedUsernames.includes(username);

  const isAllOnPageSelected =
    users.length > 0 &&
    users.every((user) => isRecipientSelected(user.username));

  return (
    /* Users Table */
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300"
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                  checked={isAllOnPageSelected}
                  disabled={isSelectingAll || isLoading}
                />
                {isSelectingAll && (
                  <span className="text-xs text-gray-500">Loading all...</span>
                )}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              No
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Username
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Full Name
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {isLoading ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                Loading users...
              </td>
            </tr>
          ) : isError ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-red-500">
                <div className="flex flex-col items-center gap-2">
                  <span className="font-semibold">Error loading users</span>
                  <span className="text-sm">
                    {error?.message || 'Unknown error occurred'}
                  </span>
                </div>
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                No users found
              </td>
            </tr>
          ) : (
            users.map((user, index) => (
              <tr
                key={user.username}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onToggleRecipient(user.username)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300"
                    checked={isRecipientSelected(user.username)}
                    onChange={() => {}}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {(currentPage - 1) * pageSize + index + 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.email || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.username}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.fullname || '-'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserTableForRecipient;
